import { useState } from 'react';
import dayjs from 'dayjs';
import LogDisplay from 'client/js/util/log_display';
import AccessLogFilters from './access_log_filters';
import { useTimeRangePicker } from 'client/js/util/use_time_range_picker';

// Validation functions for access log filters (moved from original)
const validateFilters = (filters, getTimestamps) => {
  const errors = {};

  // Hostname validation - match server-side rules
  if (filters.hostname) {
    const hostname = filters.hostname.trim();
    if (hostname.length > 100) {
      errors.hostname = 'Hostname zu lang (max. 100 Zeichen)';
    } else if (!/^[a-zA-Z0-9.\-_]+$/.test(hostname)) {
      errors.hostname = 'Ungültiges Hostname-Format (nur alphanumerisch, Punkte, Bindestriche, Unterstriche)';
    }
  }

  // IP validation - match server-side rules
  if (filters.ip) {
    const ip = filters.ip.trim();
    if (ip.length > 45) {
      errors.ip = 'IP-Filter zu lang (max. 45 Zeichen)';
    } else if (!/^[a-fA-F0-9.:]+$/.test(ip)) {
      errors.ip = 'Ungültiges IP-Format (IPv4/IPv6-Adressen und partielle Übereinstimmungen)';
    }
  }

  // Request path validation - match server-side rules
  if (filters.request_path) {
    const path = filters.request_path.trim();
    if (path.length > 500) {
      errors.request_path = 'Request-Pfad zu lang (max. 500 Zeichen)';
    } else if (!path.startsWith('/')) {
      errors.request_path = 'Request-Pfad muss mit / beginnen';
    }
  }

  // Status validation - match server-side rules (exact 3-digit HTTP status)
  if (filters.status) {
    const status = filters.status.trim();
    if (!/^[1-5]\d{2}$/.test(status)) {
      errors.status = 'Ungültiger HTTP-Statuscode (muss 3-stelliger Code wie 200, 404, 500 sein)';
    }
  }

  // Time validation - use the hook to get current timestamps
  if (filters.timeRange && getTimestamps) {
    const timestamps = getTimestamps();
    if (timestamps) {
      try {
        const parsedTime = new Date(timestamps.from_time);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

        if (isNaN(parsedTime.getTime())) {
          errors.timeRange = 'Ungültiges Zeitformat';
        } else if (parsedTime > new Date()) {
          errors.timeRange = 'Zeit kann nicht in der Zukunft liegen';
        } else if (parsedTime < sevenDaysAgo - (60 * 1000)) { // Allow 1 minute tolerance
          errors.timeRange = 'Zeit kann nicht älter als 7 Tage sein';
        }
      } catch (e) {
        errors.timeRange = 'Ungültiges Zeitformat';
      }
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

// Helper functions from original component
const getStatusClasses = (status) => {
  const suffix = status >= 200 && status < 300 ? '2xx' :
                 status >= 300 && status < 400 ? '3xx' :
                 status >= 400 && status < 500 ? '4xx' :
                 status >= 500 ? '5xx' : 'unknown';

  return {
    border: `border-status-${suffix}`,
    badge: `badge-status-${suffix}`
  };
};

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const parseRequest = (request) => {
  if (!request) return { method: 'Nicht verfügbar', path: 'Nicht verfügbar' };
  const parts = request.split(' ');
  return {
    method: parts[0] || 'Nicht verfügbar',
    path: parts[1] || 'Nicht verfügbar'
  };
};

const Index = ({ endpoint = '/usercp/access_logs', queryKey = 'access_logs' }) => {
  const [filters, setFilters] = useState({
    hostname: '',
    ip: '',
    request_path: '',
    status: '',
    timeRange: null  // NEW: Replaces from_time/to_time
  });

  // Use the time range picker hook for validation
  const { getTimestamps } = useTimeRangePicker(filters.timeRange);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      hostname: '',
      ip: '',
      request_path: '',
      status: '',
      timeRange: null
    });
  };

  // Wrap validateFilters to pass getTimestamps
  const validateFiltersWithTimestamps = (filters) => {
    return validateFilters(filters, getTimestamps);
  };

  const renderLogItem = (item) => {
    const statusClasses = getStatusClasses(item.status);
    const cleanIp = item.remote_addr?.replace(/^::ffff:/, '') || item.remote_addr;
    
    return (
      <>
        <span className="access-log-timestamp">
          {dayjs(item.time).format('DD.MM.YYYY HH:mm:ss')}
        </span>
        <span className="access-log-ip">
          {cleanIp}
        </span>
        <span className="access-log-host">
          {item.host}
        </span>
        <span className="access-log-request">
          {item.request}
        </span>
        <span className={`access-log-status ${statusClasses.badge}`}>
          {item.status}
        </span>
      </>
    );
  };

  const renderLogDetails = (item) => {
    const statusClasses = getStatusClasses(item.status);
    const cleanIp = item.remote_addr?.replace(/^::ffff:/, '') || item.remote_addr;

    return (
      <>
        <span className="access-log-details-label">Zeitstempel:</span>
        <span className="access-log-details-value">{dayjs(item.time).format('DD.MM.YYYY HH:mm:ss')}</span>

        <span className="access-log-details-label">IP-Adresse:</span>
        <span className="access-log-details-value">{cleanIp}</span>

        <span className="access-log-details-label">Host:</span>
        <span className="access-log-details-value">{item.host}</span>

        <span className="access-log-details-label">HTTP-Methode:</span>
        <span className="access-log-details-value">{parseRequest(item.request).method}</span>

        <span className="access-log-details-label">Request-Pfad:</span>
        <span className="access-log-details-value">{parseRequest(item.request).path}</span>

        <span className="access-log-details-label">Statuscode:</span>
        <span className="access-log-details-value">
          <span className={`access-log-status ${statusClasses.badge}`}>
            {item.status}
          </span>
        </span>

        <span className="access-log-details-label">SSL:</span>
        <span className="access-log-details-value">
          {item.ssl ? (
            <span className="status-positive">Ja</span>
          ) : (
            <span className="status-neutral">Nein</span>
          )}
        </span>

        <span className="access-log-details-label">HTTP-Version:</span>
        <span className="access-log-details-value">{item.http_protocol || '1.1'}</span>

        <span className="access-log-details-label">Antwortzeit:</span>
        <span className="access-log-details-value">{item.request_time?.toFixed(3)}s</span>

        <span className="access-log-details-label">Gesendete Bytes:</span>
        <span className="access-log-details-value">{formatBytes(item.bytes_sent)}</span>

        <span className="access-log-details-label">Content-Type:</span>
        <span className="access-log-details-value">{item.content_type || 'Nicht verfügbar'}</span>

        <span className="access-log-details-label">Firewall blockiert:</span>
        <span className="access-log-details-value">
          {item.blocked ? (
            <span className="status-negative">Ja</span>
          ) : (
            <span className="status-positive">Nein</span>
          )}
        </span>

        <span className="access-log-details-label">User-Agent:</span>
        <span className="access-log-details-value">{item.user_agent || 'Nicht verfügbar'}</span>
      </>
    );
  };

  return (
    <LogDisplay
      endpoint={endpoint}
      queryKey={queryKey}
      filters={filters}
      onFilterChange={handleFilterChange}
      validateFilters={validateFiltersWithTimestamps}
      clearFilters={clearFilters}
      renderLogItem={renderLogItem}
      renderLogDetails={renderLogDetails}
      FilterSection={AccessLogFilters}
    />
  );
};

export default Index;
